import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Calendar, Clock, Users, Trash2, Plus, MapPin, Navigation } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  schedulingSettingsAPI,
  scheduleBlackoutsAPI,
  usersAPI
} from '@shared/apiClient';

const WEEKDAYS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' },
];

const REASON_LABELS = {
  needs_geocoding: 'Needs geocoding',
  no_capacity_or_slot: 'No capacity or slot',
  no_capable_technician: 'No capable technician',
  no_cooling_capable_technician: 'No tech for this cooling type',
  no_allowed_technician: 'Blocked by technician rules',
  no_technicians_enabled: 'No technicians enabled',
  no_work_days: 'No work days in range',
};

const STRATEGY_OPTIONS = [
  { id: 'minimize_travel', label: 'Minimize travel time' },
  { id: 'maximize_visits', label: 'Maximize visits per day' },
  { id: 'farthest_first', label: 'Farthest from shop first' },
  { id: 'closest_first', label: 'Closest to shop first' },
  { id: 'balanced_cluster', label: 'Balanced geographic clusters' },
];

export default function SchedulingSettingsPanel() {
  const queryClient = useQueryClient();
  const [blackoutForm, setBlackoutForm] = useState({
    startDate: '',
    endDate: '',
    reason: '',
    technicianId: '',
  });

  const { data: settingsData, isLoading: settingsLoading } = useQuery(
    'schedulingSettings',
    () => schedulingSettingsAPI.get()
  );

  const { data: blackoutsData, isLoading: blackoutsLoading } = useQuery(
    'scheduleBlackouts',
    () => scheduleBlackoutsAPI.getAll()
  );

  const { data: techsData } = useQuery(
    ['users', 'technicians'],
    () => usersAPI.getUsers({ role: 'technician', limit: 100 })
  );

  const settings = settingsData?.data?.settings;
  const blackouts = blackoutsData?.data?.blackouts || [];
  const technicians = (techsData?.data?.users || []).filter((u) => u.isActive);

  const [form, setForm] = useState(null);

  const syncForm = (s) => {
    if (!s) return;
    setForm({
      allowedWeekdays: s.allowedWeekdays || [1, 2, 3, 4, 5],
      shopAddress: s.shopAddress ?? '',
      serviceMinutes: s.serviceMinutes ?? 90,
      inspectionMinutes: s.inspectionMinutes ?? 45,
      defaultDailyWorkMinutes: s.defaultDailyWorkMinutes ?? 480,
      maxLegTravelMinutes: s.maxLegTravelMinutes ?? '',
      returnToShop: s.returnToShop ?? false,
      avgSpeedMph: s.avgSpeedMph ?? 45,
      useTravelTime: s.useTravelTime ?? true,
      defaultStrategy: s.defaultStrategy ?? 'minimize_travel',
    });
  };

  if (settings && !form) {
    syncForm(settings);
  }

  const updateSettingsMutation = useMutation(
    (data) => schedulingSettingsAPI.update(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('schedulingSettings');
        toast.success('Scheduling settings saved');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to save settings');
      },
    }
  );

  const createBlackoutMutation = useMutation(
    (data) => scheduleBlackoutsAPI.create(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('scheduleBlackouts');
        setBlackoutForm({ startDate: '', endDate: '', reason: '', technicianId: '' });
        toast.success('Blackout added');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to add blackout');
      },
    }
  );

  const deleteBlackoutMutation = useMutation(
    (id) => scheduleBlackoutsAPI.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('scheduleBlackouts');
        toast.success('Blackout removed');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to remove blackout');
      },
    }
  );

  const toggleWeekday = (day) => {
    setForm((prev) => {
      const days = prev.allowedWeekdays.includes(day)
        ? prev.allowedWeekdays.filter((d) => d !== day)
        : [...prev.allowedWeekdays, day].sort((a, b) => a - b);
      return { ...prev, allowedWeekdays: days.length > 0 ? days : [day] };
    });
  };

  const handleSaveSettings = (e) => {
    e.preventDefault();
    updateSettingsMutation.mutate({
      allowedWeekdays: form.allowedWeekdays,
      shopAddress: form.shopAddress?.trim() || null,
      serviceMinutes: Number(form.serviceMinutes),
      inspectionMinutes: Number(form.inspectionMinutes),
      defaultDailyWorkMinutes: Number(form.defaultDailyWorkMinutes),
      maxLegTravelMinutes:
        form.maxLegTravelMinutes === '' || form.maxLegTravelMinutes == null
          ? null
          : Number(form.maxLegTravelMinutes),
      returnToShop: form.returnToShop,
      avgSpeedMph: Number(form.avgSpeedMph),
      useTravelTime: form.useTravelTime,
      defaultStrategy: form.defaultStrategy,
    });
  };

  const handleAddBlackout = (e) => {
    e.preventDefault();
    if (!blackoutForm.startDate || !blackoutForm.endDate) {
      toast.error('Start and end dates are required');
      return;
    }
    createBlackoutMutation.mutate({
      startDate: blackoutForm.startDate,
      endDate: blackoutForm.endDate,
      reason: blackoutForm.reason || undefined,
      technicianId: blackoutForm.technicianId || null,
    });
  };

  if (settingsLoading || !form) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center mb-4">
          <Clock className="h-5 w-5 text-gray-600 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Scheduling Rules</h2>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Auto-schedule builds each tech's day as a drive-time route from the shop. After stops are
          assigned, a route optimizer reorders visits to reduce total drive time. Choose a default
          optimization strategy below; dispatchers can override it per run in Auto-Schedule.
        </p>

        <form onSubmit={handleSaveSettings} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Allowed weekdays</label>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => toggleWeekday(value)}
                  className={`px-3 py-1.5 text-sm rounded-md border ${
                    form.allowedWeekdays.includes(value)
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 inline-flex items-center gap-1.5">
              <MapPin className="h-4 w-4 text-gray-500" />
              Shop / start address
            </label>
            <input
              type="text"
              value={form.shopAddress}
              onChange={(e) => setForm({ ...form, shopAddress: e.target.value })}
              placeholder="123 Main St, Jackson, MS"
              className="input w-full"
            />
            <p className="mt-1 text-xs text-gray-500">
              {settings?.shopLatitude != null && settings?.shopLongitude != null
                ? `Geocoded ✓ (${Number(settings.shopLatitude).toFixed(4)}, ${Number(settings.shopLongitude).toFixed(4)}). Each tech's route starts and is measured from here.`
                : 'Saved addresses are geocoded automatically. Without a shop, routing seeds from the farthest client instead.'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Service visit (min)</label>
              <input
                type="number"
                min="5"
                max="600"
                value={form.serviceMinutes}
                onChange={(e) => setForm({ ...form, serviceMinutes: Number(e.target.value) })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Inspection visit (min)</label>
              <input
                type="number"
                min="5"
                max="600"
                value={form.inspectionMinutes}
                onChange={(e) => setForm({ ...form, inspectionMinutes: Number(e.target.value) })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Daily work budget (min)</label>
              <input
                type="number"
                min="30"
                max="1440"
                step="15"
                value={form.defaultDailyWorkMinutes}
                onChange={(e) => setForm({ ...form, defaultDailyWorkMinutes: Number(e.target.value) })}
                className="input w-full"
              />
              <p className="mt-1 text-xs text-gray-500">{Math.round((form.defaultDailyWorkMinutes / 60) * 10) / 10} h/day. Per-tech overrides live on each user.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max single drive (min, optional)</label>
              <input
                type="number"
                min="1"
                max="600"
                value={form.maxLegTravelMinutes}
                onChange={(e) => setForm({ ...form, maxLegTravelMinutes: e.target.value })}
                placeholder="No cap"
                className="input w-full"
              />
              <p className="mt-1 text-xs text-gray-500">Skip a stop if the drive to it exceeds this. Leave blank for no cap.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fallback avg speed (mph)</label>
              <input
                type="number"
                min="5"
                max="90"
                value={form.avgSpeedMph}
                onChange={(e) => setForm({ ...form, avgSpeedMph: Number(e.target.value) })}
                className="input w-full"
              />
              <p className="mt-1 text-xs text-gray-500">Used only when road travel time is off or unavailable.</p>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Default optimization strategy</label>
            <select
              value={form.defaultStrategy}
              onChange={(e) => setForm({ ...form, defaultStrategy: e.target.value })}
              className="input w-full max-w-md"
            >
              {STRATEGY_OPTIONS.map(({ id, label }) => (
                <option key={id} value={id}>{label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500">
              Pre-selects the strategy in Dispatch → Auto-Schedule. Dispatchers can compare multiple strategies before applying.
            </p>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.useTravelTime}
                onChange={(e) => setForm({ ...form, useTravelTime: e.target.checked })}
                className="rounded border-gray-300 text-blue-600"
              />
              <Navigation className="h-4 w-4 text-gray-500" />
              Use road travel time (falls back to straight-line if the routing service is down)
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.returnToShop}
                onChange={(e) => setForm({ ...form, returnToShop: e.target.checked })}
                className="rounded border-gray-300 text-blue-600"
              />
              Reserve time for the drive back to the shop at day's end
            </label>
          </div>

          <button type="submit" className="btn btn-primary" disabled={updateSettingsMutation.isLoading}>
            {updateSettingsMutation.isLoading ? 'Saving...' : 'Save scheduling rules'}
          </button>
        </form>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center mb-4">
          <Calendar className="h-5 w-5 text-gray-600 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Blackouts & Time Off</h2>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Block company-wide holidays or individual technician vacations from auto-scheduling.
        </p>

        <form onSubmit={handleAddBlackout} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          <input
            type="date"
            value={blackoutForm.startDate}
            onChange={(e) => setBlackoutForm({ ...blackoutForm, startDate: e.target.value })}
            className="input"
            required
          />
          <input
            type="date"
            value={blackoutForm.endDate}
            onChange={(e) => setBlackoutForm({ ...blackoutForm, endDate: e.target.value })}
            className="input"
            required
          />
          <select
            value={blackoutForm.technicianId}
            onChange={(e) => setBlackoutForm({ ...blackoutForm, technicianId: e.target.value })}
            className="input"
          >
            <option value="">Company-wide (holiday)</option>
            {technicians.map((t) => (
              <option key={t.id} value={t.id}>{t.firstName} {t.lastName}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Reason (optional)"
            value={blackoutForm.reason}
            onChange={(e) => setBlackoutForm({ ...blackoutForm, reason: e.target.value })}
            className="input"
          />
          <button type="submit" className="btn btn-secondary inline-flex items-center justify-center gap-1" disabled={createBlackoutMutation.isLoading}>
            <Plus className="h-4 w-4" />
            Add
          </button>
        </form>

        {blackoutsLoading ? (
          <div className="text-sm text-gray-500">Loading blackouts...</div>
        ) : blackouts.length === 0 ? (
          <div className="text-sm text-gray-500">No blackouts configured.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Dates</th>
                  <th>Scope</th>
                  <th>Reason</th>
                  <th />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {blackouts.map((b) => (
                  <tr key={b.id}>
                    <td>{b.startDate} — {b.endDate}</td>
                    <td>
                      {b.technician
                        ? `${b.technician.firstName} ${b.technician.lastName}`
                        : 'Company-wide'}
                    </td>
                    <td className="text-gray-600">{b.reason || '—'}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => deleteBlackoutMutation.mutate(b.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Remove"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
        <div className="flex items-center gap-2 font-medium text-gray-800 mb-1">
          <Users className="h-4 w-4" />
          Unplaced item reasons
        </div>
        <ul className="list-disc list-inside space-y-0.5">
          {Object.entries(REASON_LABELS).map(([key, label]) => (
            <li key={key}>{label}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
